import {
  type FC,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Color from 'color';
import randomcolor from 'randomcolor';
import {length} from 'stringz';

import {ThemeContext} from '@yourcoach/shared/styles';

import styles from './AvatarPlaceholder.module.css';

interface Props {
  name?: string;
}

const AvatarPlaceholder: FC<Props> = ({name}) => {
  const [size, setSize] = useState(0);

  const {theme} = useContext(ThemeContext);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      setSize(ref.current.getBoundingClientRect().height);
    }
  }, []);

  const letters = useMemo(
    () =>
      name
        ? name
            .replace(/[@#!$%^&*()+|~=`{}[\]:";'<>?,./\d]/g, '')
            // eslint-disable-next-line regexp/strict
            .split(/[\s-_]/)
            .filter(Boolean)
            .slice(0, 2)
            .filter(Boolean)
            .map(part => (length(part) > 1 ? part[0]! : part).toUpperCase())
        : '...',
    [name],
  );

  const lettersStyle = useMemo(
    () => ({
      fontSize: `${size * 0.375}px`,
      lineHeight: `${size}px`,
    }),
    [size],
  );

  const colors = useMemo(() => {
    const firstColor = name
      ? randomcolor({
          seed: name,
        })
      : theme.color.v2.global1;

    const secondColor = Color(firstColor)
      .rotate(-20)
      .darken(0.2)
      .rgb()
      .string();

    return [firstColor, secondColor];
  }, [name, theme]);

  return (
    <div
      className={`AvatarPlaceholder ${styles.AvatarPlaceholder}`}
      ref={ref}
      style={useMemo(
        () => ({
          background: `linear-gradient(45deg, ${colors[0]}, ${colors[1]})`,
        }),
        [colors],
      )}
    >
      {size ? (
        <span className={styles.letters} style={lettersStyle}>
          {letters}
        </span>
      ) : null}
    </div>
  );
};

export default memo(AvatarPlaceholder);
